// Role definitions and smart defaults for the Weekly Staff Forecast feature.
//
// All wages are USD baselines that get converted via convertFromUSD() to the
// venue's local currency at display time. Recommended counts are heuristics
// based on a mid-sized venue at "normal" demand — they scale up/down with
// covers, occupancy, and the busy/quiet scenario toggle.

export type Shift = "morning" | "afternoon" | "evening";

export const SHIFTS: { id: Shift; label: string; hours: string }[] = [
  { id: "morning", label: "Morning", hours: "7am–2pm" },
  { id: "afternoon", label: "Afternoon", hours: "2pm–6pm" },
  { id: "evening", label: "Evening", hours: "6pm–close" },
];

export const SHIFT_HOURS: Record<Shift, number> = {
  morning: 7,
  afternoon: 4,
  evening: 6,
};

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export type DayKey = typeof DAYS[number];

// Weekend bias — used as default occupancy/cover multipliers vs weekday baseline.
export const DAY_DEMAND_MULTIPLIER: Record<DayKey, number> = {
  Mon: 0.75,
  Tue: 0.80,
  Wed: 0.85,
  Thu: 1.00,
  Fri: 1.20,
  Sat: 1.35,
  Sun: 1.10,
};

// Lunch / dinner peak bias — applied per shift to the recommended staff count.
export const SHIFT_PEAK_MULTIPLIER: Record<Shift, number> = {
  morning: 0.90, // breakfast / prep
  afternoon: 0.70, // off-peak lull
  evening: 1.20, // dinner peak
};

export type Scenario = "quiet" | "normal" | "busy";
export const SCENARIO_MULTIPLIER: Record<Scenario, number> = {
  quiet: 0.75,
  normal: 1.0,
  busy: 1.30,
};

export interface RoleDef {
  /** Stable id used as map key */
  id: string;
  title: string;
  /** USD/hr baseline. Display via convertFromUSD(). */
  hourlyWageUSD: number;
  /** Which shifts this role typically works. */
  shifts: Shift[];
  /** Base headcount per shift at "normal" demand for a mid-sized venue. */
  baseCount: number;
}

// ─── Roles by venue type ──────────────────────────────────────────────────
// Source: user spec. Wages are reasonable mid-market USD baselines; users
// can override per role in the UI.
export const ROLES_BY_VENUE: Record<string, RoleDef[]> = {
  restaurant: [
    { id: "head-chef",     title: "Head Chef",     hourlyWageUSD: 35, shifts: ["morning", "evening"],            baseCount: 1 },
    { id: "sous-chef",     title: "Sous Chef",     hourlyWageUSD: 25, shifts: ["morning", "evening"],            baseCount: 1 },
    { id: "line-cook",     title: "Line Cook",     hourlyWageUSD: 18, shifts: ["morning", "evening"],            baseCount: 2 },
    { id: "prep-cook",     title: "Prep Cook",     hourlyWageUSD: 16, shifts: ["morning", "afternoon"],          baseCount: 1 },
    { id: "server",        title: "Server",        hourlyWageUSD: 15, shifts: ["morning", "afternoon", "evening"], baseCount: 3 },
    { id: "host",          title: "Host",          hourlyWageUSD: 14, shifts: ["evening"],                       baseCount: 1 },
    { id: "runner",        title: "Runner",        hourlyWageUSD: 14, shifts: ["evening"],                       baseCount: 1 },
    { id: "bartender",     title: "Bartender",     hourlyWageUSD: 18, shifts: ["evening"],                       baseCount: 1 },
    { id: "floor-manager", title: "Floor Manager", hourlyWageUSD: 24, shifts: ["evening"],                       baseCount: 1 },
  ],
  "coffee-shop": [
    { id: "barista",        title: "Barista",       hourlyWageUSD: 16, shifts: ["morning", "afternoon"], baseCount: 2 },
    { id: "cafe-manager",   title: "Café Manager",  hourlyWageUSD: 22, shifts: ["morning"],              baseCount: 1 },
    { id: "kitchen-hand",   title: "Kitchen Hand",  hourlyWageUSD: 15, shifts: ["morning"],              baseCount: 1 },
    { id: "cashier",        title: "Cashier",       hourlyWageUSD: 14, shifts: ["morning", "afternoon"], baseCount: 1 },
    { id: "server-cafe",    title: "Server",        hourlyWageUSD: 14, shifts: ["morning", "afternoon"], baseCount: 1 },
  ],
  bar: [
    { id: "head-bartender", title: "Head Bartender", hourlyWageUSD: 24, shifts: ["evening"],              baseCount: 1 },
    { id: "bartender-bar",  title: "Bartender",      hourlyWageUSD: 18, shifts: ["evening"],              baseCount: 2 },
    { id: "barback",        title: "Barback",        hourlyWageUSD: 14, shifts: ["evening"],              baseCount: 1 },
    { id: "server-bar",     title: "Server",         hourlyWageUSD: 15, shifts: ["evening"],              baseCount: 2 },
    { id: "door-host",      title: "Door Host",      hourlyWageUSD: 16, shifts: ["evening"],              baseCount: 1 },
    { id: "floor-manager-bar", title: "Floor Manager", hourlyWageUSD: 24, shifts: ["evening"],            baseCount: 1 },
  ],
  hotel: [
    { id: "restaurant-manager", title: "Restaurant Manager", hourlyWageUSD: 28, shifts: ["morning", "evening"],            baseCount: 1 },
    { id: "head-chef-hotel",    title: "Head Chef",          hourlyWageUSD: 35, shifts: ["morning", "evening"],            baseCount: 1 },
    { id: "sous-chef-hotel",    title: "Sous Chef",          hourlyWageUSD: 25, shifts: ["morning", "evening"],            baseCount: 1 },
    { id: "waiter",             title: "Waiter",             hourlyWageUSD: 15, shifts: ["morning", "afternoon", "evening"], baseCount: 3 },
    { id: "room-service",       title: "Room Service Attendant", hourlyWageUSD: 16, shifts: ["morning", "evening"],        baseCount: 1 },
    { id: "banquet-staff",      title: "Banquet Staff",      hourlyWageUSD: 16, shifts: ["evening"],                       baseCount: 2 },
  ],
  bakery: [
    { id: "head-baker",    title: "Head Baker",    hourlyWageUSD: 22, shifts: ["morning"],              baseCount: 1 },
    { id: "pastry-chef",   title: "Pastry Chef",   hourlyWageUSD: 20, shifts: ["morning"],              baseCount: 1 },
    { id: "counter-staff", title: "Counter Staff", hourlyWageUSD: 14, shifts: ["morning", "afternoon"], baseCount: 2 },
    { id: "cashier-bakery",title: "Cashier",       hourlyWageUSD: 14, shifts: ["morning", "afternoon"], baseCount: 1 },
  ],
  "fast-casual": [
    { id: "kitchen-lead",  title: "Kitchen Lead",  hourlyWageUSD: 20, shifts: ["morning", "evening"],            baseCount: 1 },
    { id: "line-cook-fc",  title: "Line Cook",     hourlyWageUSD: 17, shifts: ["morning", "evening"],            baseCount: 2 },
    { id: "cashier-fc",    title: "Cashier",       hourlyWageUSD: 14, shifts: ["morning", "afternoon", "evening"], baseCount: 1 },
    { id: "runner-fc",     title: "Runner",        hourlyWageUSD: 14, shifts: ["afternoon", "evening"],          baseCount: 1 },
    { id: "shift-manager", title: "Shift Manager", hourlyWageUSD: 22, shifts: ["morning", "evening"],            baseCount: 1 },
  ],
};

// Map onboarding business types to forecast venue keys.
const VENUE_ALIAS: Record<string, keyof typeof ROLES_BY_VENUE> = {
  restaurant: "restaurant",
  pizzeria: "restaurant",
  "ghost-kitchen": "fast-casual",
  "food-truck": "fast-casual",
  "fast-casual": "fast-casual",
  "coffee-shop": "coffee-shop",
  cafe: "coffee-shop",
  café: "coffee-shop",
  "juice-bar": "coffee-shop",
  bar: "bar",
  bakery: "bakery",
  hotel: "hotel",
  "bed-and-breakfast": "hotel",
};

export function venueKeyFromBusinessType(bt: string | undefined | null): keyof typeof ROLES_BY_VENUE {
  if (!bt) return "restaurant";
  return VENUE_ALIAS[bt] ?? "restaurant";
}

export function getRolesForBusinessType(bt: string | undefined | null): RoleDef[] {
  return ROLES_BY_VENUE[venueKeyFromBusinessType(bt)] ?? ROLES_BY_VENUE.restaurant;
}

/**
 * Recommended headcount for a role on a given day/shift, taking into account:
 * - the role's base count
 * - shift peak bias (lunch vs dinner)
 * - day-of-week demand bias (weekend lift)
 * - scenario multiplier (quiet/normal/busy)
 * - covers/occupancy index (1.0 = baseline)
 *
 * Always returns at least 1 if the role works that shift, 0 otherwise.
 */
export function recommendedCount(
  role: RoleDef,
  shift: Shift,
  day: DayKey,
  scenario: Scenario,
  demandIndex: number,
): number {
  if (!role.shifts.includes(shift)) return 0;
  const raw =
    role.baseCount *
    SHIFT_PEAK_MULTIPLIER[shift] *
    DAY_DEMAND_MULTIPLIER[day] *
    SCENARIO_MULTIPLIER[scenario] *
    Math.max(0.4, demandIndex);
  return Math.max(1, Math.round(raw));
}

/**
 * Smart default per-day expected covers for a venue, based on weekday/weekend
 * bias. We pick a sensible baseline by venue type (e.g. cafés do more lunch,
 * bars do more weekend evenings) and let users override.
 */
export function defaultDailyCovers(bt: string | undefined | null): Record<DayKey, number> {
  const venue = venueKeyFromBusinessType(bt);
  const baseline: Record<typeof venue, number> = {
    restaurant: 120,
    "coffee-shop": 200,
    bar: 150,
    hotel: 180,
    bakery: 220,
    "fast-casual": 250,
  };
  const base = baseline[venue] ?? 120;
  return DAYS.reduce((acc, d) => {
    acc[d] = Math.round(base * DAY_DEMAND_MULTIPLIER[d]);
    return acc;
  }, {} as Record<DayKey, number>);
}

/** Smart default per-day projected revenue (USD), derived from covers × avg ticket. */
export function defaultDailyRevenue(bt: string | undefined | null): Record<DayKey, number> {
  const venue = venueKeyFromBusinessType(bt);
  const avgTicketUSD: Record<typeof venue, number> = {
    restaurant: 45,
    "coffee-shop": 8,
    bar: 28,
    hotel: 60,
    bakery: 12,
    "fast-casual": 15,
  };
  const ticket = avgTicketUSD[venue] ?? 30;
  const covers = defaultDailyCovers(bt);
  return DAYS.reduce((acc, d) => {
    acc[d] = Math.round(covers[d] * ticket);
    return acc;
  }, {} as Record<DayKey, number>);
}
